import { Alert, AlertTitle, Button } from '@mui/material';
import { BulkActivationResultItemDTO } from 'probonio-shared-ui/api';
import { downloadFile } from 'probonio-shared-ui/module/api';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicDialog, DialogState } from '../../../component/dialog';

interface Props {
  dialogState: DialogState;
  variant: 'deactivate' | 'pause';
  employees: BulkActivationResultItemDTO[];
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export const BulkActivationResultDialog: React.FC<Props> = ({ dialogState, variant, employees }) => {
  const { t } = useTranslation('benefitsModule');

  const variantKey = variant === 'deactivate' ? 'bulkEditDialog.deactivateResultDialog' : 'bulkEditDialog.pauseResultDialog';
  const sharedKey = 'bulkEditDialog.editActivationsResultDialog';

  const handleDownload = useCallback((): void => {
    const header = [
      t(`${sharedKey}.csvHeaderFirstName`),
      t(`${sharedKey}.csvHeaderLastName`),
      t(`${sharedKey}.csvHeaderMail`),
      t(`${sharedKey}.csvHeaderEmployeeNumber`),
      t(`${sharedKey}.csvHeaderDepartment`),
      t(`${sharedKey}.csvHeaderExternalId`),
      t(`${sharedKey}.csvHeaderStartDate`),
      t(`${sharedKey}.csvHeaderEndDate`),
    ];
    const rows = employees.map(emp => [
      emp.targetFirstName ?? '',
      emp.targetLastName ?? '',
      emp.targetMail ?? '',
      emp.targetEmployeeNumber ?? '',
      emp.targetDepartment ?? '',
      emp.targetExternalId ?? '',
      emp.startDate ?? '',
      emp.endDate ?? '',
    ]);
    const BOM_UTF8 = '\uFEFF'; // UTF-8 BOM ensures Excel on Windows opens umlauts correctly
    const csv = BOM_UTF8 + [header, ...rows].map(row => row.map(escapeCsvField).join(',')).join('\n');
    downloadFile(t(`${variantKey}.csvFilename`), csv, 'text/csv;charset=utf-8');
  }, [t, sharedKey, variantKey, employees]);

  return (
    <BasicDialog
      dialogState={dialogState}
      title={t(`${variantKey}.title`)}
      content={
        <Alert severity={variant === 'pause' ? 'error' : 'warning'}>
          <AlertTitle>{t(`${sharedKey}.messageTitle`)}</AlertTitle>
          {t(`${variantKey}.message`, { count: employees.length })}
        </Alert>
      }
      actions={
        <>
          <Button
            type="button"
            onClick={dialogState.handleClose}
            sx={{ marginRight: 2 }}
            variant="text"
            data-test-id="modal-bikeleasing-customer-dialog-button-close"
          >
            {t('common:buttons.close')}
          </Button>
          <Button type="button" variant="contained" onClick={handleDownload}>
            {t(`${sharedKey}.downloadButton`)}
          </Button>
        </>
      }
    />
  );
};
